import { ChangeDetectionStrategy, Component, Input, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { FiChecklistScheduleType } from '../../../fi-checklist-schedule-type';
import { FiChecklistTimeIntervalType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import { IChecklistTimeInterval, IChecklistTimeIntervalGroup } from '@fi-shared/checklist/checklist-v2';
import { FiChecklist2ScheduleFormatService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-format.service';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiToggleSwitchLabelAlign } from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';

@Component({
    selector: 'fi-checklist2-time-intervals-viewer',
    templateUrl: './time-intervals-viewer.component.html',
    styleUrls: ['./time-intervals-viewer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-time-intervals-viewer' },
    standalone: false,
})
export class FiChecklist2TimeIntervalsViewerComponent {
    @Input() intervals: IChecklistTimeInterval[];
    @Input() intervalType: FiChecklistTimeIntervalType;
    @Input() intervalGroup: IChecklistTimeIntervalGroup = {};
    public scheduleType = input<FiChecklistScheduleType>();

    protected ObjectKeys = Object.keys;

    private fiChecklist2ScheduleFormatService = inject(FiChecklist2ScheduleFormatService);
    private fiChecklist2ScheduleIntervalService = inject(FiChecklist2ScheduleIntervalService);
    private appTextService = inject(AppTextService);

    protected isRecurringSchedule = computed(() => this.scheduleType() === FiChecklistScheduleType.Recurring);


    private readonly periodTypeToClassMap: Partial<Record<FiChecklistTimeIntervalType, string>> = {
        [FiChecklistTimeIntervalType.RecurringWeekly]: 'weekly',
        [FiChecklistTimeIntervalType.RecurringMonthly]: 'monthly',
        [FiChecklistTimeIntervalType.RecurringYearly]: 'yearly',
        [FiChecklistTimeIntervalType.OneTime]: 'one-time',
    };

    protected getPeriodName(periodIndex: number): string {
        return this.fiChecklist2ScheduleFormatService.getPeriodName(this.intervalType, periodIndex);
    }

    protected getPeriodNameAdvanced(interval: IChecklistTimeInterval, isStart?: boolean): string {
        return this.fiChecklist2ScheduleFormatService.getPeriodNameAdvanced(this.intervalType, interval, isStart);
    }

    protected getPeriodNameClass(): string {
        return this.periodTypeToClassMap[this.intervalType] || '';
    }

    protected getUntilLabel(): string {
        return this.appTextService.get(this.isAuditOrOnDemandInterval() ? 'common.close_after' : 'common.until');
    }

    protected isEnabledInterval(intervalGroupKey: string): boolean {
        return this.intervalGroup[intervalGroupKey][0].IsEnabled || this.isAuditOrOnDemandInterval();
    }

    protected isAuditOrOnDemandInterval(): boolean {
        return this.fiChecklist2ScheduleIntervalService.isAuditOrOnDemandInterval(this.intervalType);
    }

    protected getAuditIntervalStartDate(interval: IChecklistTimeInterval): string {
        return this.fiChecklist2ScheduleFormatService.getAuditIntervalStartDate(interval);
    }

    protected isDailyInterval(): boolean {
        return this.fiChecklist2ScheduleIntervalService.isDailyInterval(this.intervalType);
    }

    protected getStartTime(interval: IChecklistTimeInterval): string {
        return this.isAuditOrOnDemandInterval()
            ? this.getAuditIntervalStartDate(interval)
            : this.fiChecklist2ScheduleFormatService.getHourMinuteInputData(interval.Start);
    }

    protected getEndTime(interval: IChecklistTimeInterval): string {
        return this.fiChecklist2ScheduleFormatService.getHourMinuteInputData(interval.End);
    }

    protected readonly FiToggleSwitchLabelAlign = FiToggleSwitchLabelAlign;
}
